<template>
  <div>
    <NavBar>
      <template v-slot:desktop-nav>
        <div v-cloak class="d-flex">
          <ItemSearch v-if="userRole && hasRole(Role.MEMBER)" :listId="listId" :loading="isLoading" />
          <router-link v-if="userRole && hasRole(Role.MEMBER)" class="btn btn-yellow btn-nav my-2 my-sm-0 ml-2" :to="'/lists/' + listId + '/order'">
            <i class="fas fa-sort-amount-up"></i>
          </router-link>
        </div>
      </template>
      <template v-slot:mobile-nav>
        <router-link class="mobile-nav-icon btn-nav btn btn-yellow w-100" :to="'/lists/' + listId">
          <i class="fas fa-arrow-alt-circle-left"></i>
          Back
        </router-link>
        <router-link v-if="userRole && hasRole(Role.MEMBER)" class="mobile-nav-icon btn-nav-yellow btn btn-yellow w-100" :to="'/lists/' + listId + '/order'">
          <i class="fas fa-sort-amount-up"></i>
          Order
        </router-link>
      </template>
    </NavBar>
    <div v-cloak class="container home">
      <div class="movie-list-header">
        <h1>Search: {{ query }}</h1>
      </div>
      <ItemSearch v-if="userRole && hasRole(Role.MEMBER)" class="w-100 mb-3 d-md-none" :listId="listId" :loading="isLoading" />
      <div v-if="hasAddedItems" class="alert alert-success add-next-actions" role="status">
        <span>Title added. What would you like to do next?</span>
        <div class="add-next-action-buttons">
          <router-link class="btn btn-yellow" :to="'/lists/' + listId">Back to list</router-link>
          <router-link class="btn btn-green" :to="'/lists/' + listId + '/order'">{{ orderActionLabel }}</router-link>
        </div>
      </div>
      <div v-if="isLoading" class="search-status text-center" role="status" aria-live="polite">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        Searching for titles…
      </div>
      <div v-else-if="searchError" class="alert alert-danger search-status" role="alert">
        <p class="mb-2">{{ searchError }}</p>
        <button type="button" class="btn btn-yellow" @click="loadResults">Try again</button>
      </div>
      <div class="row search-group" v-else-if="listId" v-cloak>
        <div class="col-xl-4 col-md-6" v-for="item in items" :key="item.imdbID">
          <SearchItem
            :item="item"
            :addState="addStates[item.imdbID] || (item.isInList ? 'already' : 'default')"
            @add-item="addItem"
            @remove-item="removeItem"
            :listId="listId"
            :canRemove="hasRole(Role.OWNER)" />
        </div>
        <div class="col-12 search-status text-center" v-if="items && items.length === 0" role="status">
          <h2>No results found.</h2>
          <p>Try another movie or TV series title.</p>
        </div>
      </div>
      <div v-else>
        <h4>Invalid ListId</h4>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import ItemSearch from "@/components/ItemSearch.vue";
import SearchItem from "@/components/SearchItem.vue";
import ItemService from "../services/ItemService";
import ListService from "../services/ListService";
import { Role, hasRole } from "../utilities/list.role";
import ListType from "../utilities/listTypes";

export default {
  name: "ItemsView",
  data() {
    return {
      items: null,
      Role,
      userRole: null,
      list: null,
      addStates: {},
      addedItemIds: {},
      isLoading: false,
      searchError: "",
      requestId: 0,
    };
  },
  components: {
    NavBar,
    ItemSearch,
    SearchItem,
  },
  computed: {
    listId: function () {
      return this.$route.query.listId;
    },
    query: function () {
      return this.$route.query.query;
    },
    hasAddedItems: function () {
      return Object.keys(this.addedItemIds).length > 0;
    },
    orderActionLabel: function () {
      return this.list && Number(this.list.listTypeId) === ListType.RANKED ? "Rank added titles" : "Order added titles";
    },
  },
  methods: {
    hasRole(role) {
      return hasRole(this.userRole.id, role);
    },
    addItem(item) {
      if (this.addStates[item.imdbID] === "adding" || item.isInList) return;

      this.addStates[item.imdbID] = "adding";

      return ListService.addItemToList(this.listId, item.imdbID)
        .then((result) => {
          if (result == "success") {
            item.isInList = true;
            this.addStates[item.imdbID] = "added";
            this.addedItemIds[item.imdbID] = true;
            this.$toast.success("Added " + item.Title);
          } else {
            this.addStates[item.imdbID] = "failed";
            this.$toast.error("Could not add " + item.Title);
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 409) {
            item.isInList = true;
            this.addStates[item.imdbID] = "already";
            this.$toast.warning(item.Title + " is already in the list");
            return;
          }

          this.addStates[item.imdbID] = "failed";

          if (error.response && error.response.status === 401 && error.handleError) {
            return error.handleError();
          }

          if (error.response && error.response.status === 403) {
            this.$toast.warning(error.response.data.message || "You do not have permission to add this title");
            return;
          }

          this.$toast.error("Could not add " + item.Title + ". Please try again.");
        });
    },
    removeItem(item) {
      if (!this.hasRole(Role.OWNER)) return;

      ListService.removeItemFromList(this.listId, item.imdbID)
        .then((result) => {
          if (result == "success") {
            item.isInList = false;
            this.addStates[item.imdbID] = "default";
            delete this.addedItemIds[item.imdbID];
            this.$toast.success("Removed " + item.Title);
          } else {
            this.$toast.error("Error removing " + item.Title);
          }
        })
        .catch((error) => {
          return error.handleError();
        });
    },
    loadResults() {
      const query = (this.query || "").trim();
      const requestId = ++this.requestId;
      let listLoaded = false;

      if (!query) {
        this.items = [];
        this.searchError = "Enter a title to search.";
        this.isLoading = false;
        return;
      }

      this.items = null;
      this.searchError = "";
      this.isLoading = true;

      ListService.getList(this.listId)
        .then((list) => {
          if (requestId !== this.requestId) return null;

          this.list = list.list;
          this.userRole = list.userRole;
          listLoaded = true;
          return ItemService.searchItems(this.listId, query);
        })
        .then((items) => {
          if (items === null || requestId !== this.requestId) return;
          this.items = items;
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;

          const status = error.response ? error.response.status : null;
          if (status === 401 && error.handleError) error.handleError();
          if (status === 403 && !listLoaded) {
            this.$router.replace({ name: "Forbidden" });
            return;
          }
          if (status === 404) {
            this.$router.replace({ name: "Missing Resource" });
            return;
          }

          this.searchError = "Search failed. Please try again.";
        })
        .finally(() => {
          if (requestId === this.requestId) this.isLoading = false;
        });
    },
  },
  watch: {
    "$route.query": {
      handler() {
        this.loadResults();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
<style scoped>
.add-next-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

.add-next-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-status {
  color: white;
  margin: 24px 0;
}

.alert.search-status {
  color: #721c24;
}

@media (max-width: 575px) {
  .add-next-action-buttons,
  .add-next-action-buttons .btn {
    width: 100%;
  }
}
</style>
